/**
 * Accessory calculator on the PDP essentials section.
 *
 * The shopper gives a room size once and every accessory card fills in the
 * amount that covers it. Each card carries its own rule, resolved server side
 * from the accessory's type:
 *
 *   area      - unit is the m² one pack/roll/tub covers
 *   perimeter - unit is the length of one piece, in metres
 *   per_pack  - unit is how many packs of flooring one of these serves
 *   fixed     - always the minimum, whatever the room size
 *
 * Only whole sale units are ever sent to the cart, so every result is rounded
 * up. Width x length gives a real perimeter; an area on its own can only
 * estimate one, hence the factor - a square room's perimeter is 4√A, and the
 * default allows for a slightly oblong room.
 *
 * Stepping is not handled here. product-essentials.js already owns the
 * delegated [data-card-qty-step] clicks and the quick-add sync; this module
 * listens for what that leaves behind and keeps the metre readout and the
 * running total in step.
 */
const ROOT = "[data-accessory-calculator]";

export function money(value) {
  return new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
    style: "currency",
    currency: window.Shopify?.currency?.active || "GBP",
  }).format(value);
}

const number = (input) => {
  const value = parseFloat(input?.value);
  return Number.isFinite(value) && value > 0 ? value : 0;
};

/** Cards live in the section, not inside the calculator element. */
function cardsFor(root) {
  const scope = root.closest(".product-essentials") || document;
  return Array.from(scope.querySelectorAll(".product-card__essentials"));
}

/**
 * Turns a room area into the three figures the rules divide by. Shared with the
 * cart accessories modal, which has an area from the cart rather than from a
 * shopper typing one.
 *
 * Wastage lifts both the area and the perimeter: underlay is cut to fit and the
 * offcuts are lost, and a trim mitred into corners loses the same way.
 */
export function deriveFromArea(area, { wastagePct = 0, perimeterFactor = 4.2, packs = 0 } = {}) {
  const uplift = 1 + (wastagePct || 0) / 100;
  const covered = (area || 0) * uplift;
  const perimeter = area > 0 ? perimeterFactor * Math.sqrt(area) * uplift : 0;

  return { area: covered, perimeter, packs };
}

export function quantityFor(card, { area, perimeter, packs }) {
  const unit = parseFloat(card.dataset.accessoryUnit) || 0;
  const min = 1;

  switch (card.dataset.accessoryMethod) {
    case "area":
      if (!area || unit <= 0) return min;
      return Math.max(min, Math.ceil(area / unit));
    case "perimeter":
      if (!perimeter || unit <= 0) return min;
      return Math.max(min, Math.ceil(perimeter / unit));
    case "per_pack":
      if (!packs || unit <= 0) return min;
      return Math.max(min, Math.ceil(packs / unit));
    default:
      return min;
  }
}

/**
 * The visible figure is derived from the cart quantity rather than stored
 * alongside it, so a shopper stepping the card by hand and a calculated
 * result can never disagree.
 */
export function renderCard(card) {
  const input = card.querySelector('input[name="quantity"]');
  if (!input) return;

  const quantity = Math.max(1, parseInt(input.value, 10) || 1);
  const display = card.querySelector("[data-accessory-display]");

  if (display && card.dataset.accessoryMethod === "perimeter") {
    const unit = parseFloat(card.dataset.accessoryUnit) || 0;
    // toFixed then strip the trailing zero: 2.4 rather than 2.40, but 10 rather
    // than 10.0 as well.
    display.textContent = String(parseFloat((quantity * unit).toFixed(2)));
  } else if (display) {
    display.textContent = String(quantity);
  }

  const button = card.querySelector("[quick-add-qf]");
  if (button) button.dataset.quantity = String(quantity);
}

function renderTotal(root) {
  const target = document
    .querySelector(".product-essentials")
    ?.querySelector("[data-essentials-total]");
  if (!target) return;

  const total = cardsFor(root).reduce((sum, card) => {
    const input = card.querySelector('input[name="quantity"]');
    const price = parseFloat(card.dataset.accessoryPrice);
    if (!input || !Number.isFinite(price)) return sum;
    return sum + (Math.max(1, parseInt(input.value, 10) || 1) * price) / 100;
  }, 0);

  // Cards without a price attribute are the plain fallback layout, which has no
  // calculator to total for - leave the server-rendered figure alone.
  if (total > 0) target.textContent = money(total);
}

function setup(root) {
  if (root.dataset.accessoryBound) return;
  root.dataset.accessoryBound = "true";

  const wastagePct = parseFloat(root.dataset.wastage) || 0;
  const perimeterFactor = parseFloat(root.dataset.perimeterFactor) || 4.2;
  const floorCoverage = parseFloat(root.dataset.floorCoverage) || 0;

  const areaInput = root.querySelector("[data-accessory-area]");
  const widthInput = root.querySelector("[data-accessory-width]");
  const lengthInput = root.querySelector("[data-accessory-length]");
  const wastage = root.querySelector("[data-accessory-wastage]");
  const calculate = root.querySelector("[data-accessory-calculate]");

  // Area is the default because it is the one figure most shoppers already
  // know. W x L is there for the exact perimeter a trim needs.
  let mode = "area";
  let touched = false;

  /**
   * Width x length gives a real perimeter; an area on its own can only estimate
   * one, which is what the factor is for. Wastage lifts both - underlay is cut
   * to fit and a trim is mitred into corners, and both lose the offcuts.
   */
  const measurements = () => {
    const width = number(widthInput);
    const length = number(lengthInput);
    const uplift = wastage?.checked ? wastagePct : 0;

    if (mode === "dimensions" && width > 0 && length > 0) {
      const exact = deriveFromArea(width * length, { wastagePct: uplift, perimeterFactor });
      // The room's own perimeter, not an estimate from its area.
      exact.perimeter = 2 * (width + length) * (1 + uplift / 100);
      exact.packs = floorCoverage > 0 ? Math.ceil(exact.area / floorCoverage) : 0;
      return exact;
    }

    if (mode !== "area") return { area: 0, perimeter: 0, packs: 0 };

    const values = deriveFromArea(number(areaInput), { wastagePct: uplift, perimeterFactor });
    // Packs come off the uplifted area for the same reason the buy box does it
    // that way - the wastage allowance is flooring that gets bought.
    values.packs =
      floorCoverage > 0 && values.area > 0 ? Math.ceil(values.area / floorCoverage) : 0;
    return values;
  };

  const selectMode = (next) => {
    mode = next;
    root.querySelectorAll("[data-accessory-tab]").forEach((tab) => {
      tab.classList.toggle("active", tab.dataset.accessoryTab === next);
    });
    root.querySelectorAll("[data-accessory-pane]").forEach((pane) => {
      pane.hidden = pane.dataset.accessoryPane !== next;
    });
  };

  const apply = () => {
    const values = measurements();
    if (!values.area && !values.perimeter) return;

    cardsFor(root).forEach((card) => {
      if (!card.dataset.accessoryMethod) return;
      const input = card.querySelector('input[name="quantity"]');
      if (!input) return;
      input.value = String(quantityFor(card, values));
      renderCard(card);
    });

    renderTotal(root);
  };

  // Any measurement the shopper makes here rather than in the buy box detaches
  // this calculator, after which it is theirs and the one above stops driving
  // it. Pressing Calculate is not a detach - it only applies what is already
  // on show, which may well have come from up there.
  root.querySelectorAll("[data-accessory-tab]").forEach((tab) => {
    tab.addEventListener("click", () => {
      touched = true;
      selectMode(tab.dataset.accessoryTab);
    });
  });

  [areaInput, widthInput, lengthInput].forEach((input) => {
    input?.addEventListener("input", () => {
      touched = true;
    });
  });

  calculate?.addEventListener("click", apply);
  // Ticking wastage after a calculation should update what is already on show,
  // rather than waiting for the button to be pressed a second time.
  wastage?.addEventListener("change", () => {
    touched = true;
    if (number(areaInput) || (number(widthInput) && number(lengthInput))) apply();
  });

  /**
   * The buy box calculator drives this one. The room is measured once, up
   * there, and the accessories follow it live - measurement type, figures and
   * wastage alike - so a shopper who never scrolls down still gets the right
   * quantities waiting for them.
   *
   * It is a one-way link in both senses: nothing done here is pushed back up,
   * because fiddling with accessories must never change how much flooring is
   * being bought, and the moment the shopper measures here instead this
   * calculator detaches and is theirs alone.
   */
  const pdp = document.querySelector("[data-product-calculator]");

  if (pdp && areaInput) {
    const pdpArea = pdp.querySelector("[data-calc-area]");
    const pdpWidth = pdp.querySelector("[data-calc-width]");
    const pdpLength = pdp.querySelector("[data-calc-length]");
    const pdpWastage = pdp.querySelector("[data-calc-wastage]");

    const pdpMode = () =>
      pdp.querySelector("[data-calc-measure-tab].active")?.dataset.calcMeasureTab || "area";

    const clearInputs = () => {
      [areaInput, widthInput, lengthInput].forEach((input) => {
        if (input) input.value = "";
      });
    };

    const follow = (nextMode) => {
      if (touched) return;

      if (wastage && pdpWastage) wastage.checked = pdpWastage.checked;

      clearInputs();
      if (nextMode === "dimensions") {
        if (widthInput) widthInput.value = pdpWidth?.value || "";
        if (lengthInput) lengthInput.value = pdpLength?.value || "";
      } else {
        areaInput.value = pdpArea?.value || "";
      }

      selectMode(nextMode);
      apply();
    };

    follow(pdpMode());

    [pdpArea, pdpWidth, pdpLength].forEach((input) => {
      input?.addEventListener("input", () => follow(pdpMode()));
      input?.addEventListener("change", () => follow(pdpMode()));
    });
    pdpWastage?.addEventListener("change", () => follow(pdpMode()));

    // Read from the clicked tab rather than the active class: this handler and
    // the buy box calculator's own both fire on the same click, in whichever
    // order they were bound, so the class may not have moved yet.
    pdp.querySelectorAll("[data-calc-measure-tab]").forEach((tab) => {
      tab.addEventListener("click", () => follow(tab.dataset.calcMeasureTab));
    });
  }

  cardsFor(root).forEach(renderCard);
}

/**
 * Delegated so cards re-rendered by a section update keep working. The stepper
 * in product-essentials.js dispatches change on the quantity field, which is
 * what lands here.
 */
function onQuantityChange(event) {
  const input = event.target.closest?.('.product-card__essentials input[name="quantity"]');
  if (!input) return;
  const root = document.querySelector(ROOT);
  if (!root) return;
  renderCard(input.closest(".product-card__essentials"));
  renderTotal(root);
}

export function initAccessoryQuantity(scope = document) {
  scope.querySelectorAll(ROOT).forEach(setup);

  if (!window.__accessoryQtyBound) {
    window.__accessoryQtyBound = true;
    document.addEventListener("change", onQuantityChange);
    document.addEventListener("input", onQuantityChange);
  }
}

/**
 * Self-initialising, the same way slider-mobile-grid does it. critical.js also
 * loads this module on [data-accessory-calculator], but setup() is idempotent
 * so whichever arrives first wins and the other is a no-op - the calculator is
 * not left dead if the loader never fires for it.
 */
function onDomReady(callback) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
  } else {
    callback();
  }
}

onDomReady(() => initAccessoryQuantity(document));

// The theme editor swaps the whole section, which takes the bound flag with it.
document.addEventListener("shopify:section:load", (event) => {
  initAccessoryQuantity(event?.target || document);
});

export default initAccessoryQuantity;
